import { ArrowRightLeft, Plus, Minus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useStepUpRefusal } from "@/shared/hooks/useStepUpRefusal";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { hasStepUpMethod, STEP_UP_EMAIL_REQUEST } from "@/shared/utils/stepUp";
import { colocaToast } from "@/shared/utils/toast";
import { isTwoFactorCodeError, stepUpRefusal } from "@/shared/utils/twoFactorErrors";

import { CardsService } from "../services/cardsService";
import type { Card, CardTransferTarget } from "../types/cards-api.types";
import { formatCardBalance } from "../utils/walletMovementFormat";
import { CardBalanceTransit } from "./card-load/CardBalanceTransit";
import { TwoFactorChallengeModal } from "./TwoFactorChallengeModal";
import { TwoFactorRequiredModal } from "./TwoFactorRequiredModal";

const PRESET_AMOUNTS = [25, 50, 100, 250, 500, 1000] as const;

export type BalanceModalMode = "load" | "withdraw" | "move";

/**
 * Which operations the card's organization has. Without the card wallet a card's balance can only be moved to
 * another card: loading from the wallet and withdrawing to it both need the wallet.
 */
function balanceModesFor(withdrawalsAllowed: boolean): BalanceModalMode[] {
  return withdrawalsAllowed ? ["load", "withdraw", "move"] : ["move"];
}

interface CardBalanceModalProps {
  isOpen: boolean;
  initialMode: BalanceModalMode;
  card: Card;
  walletAvailable: number;
  cardAvailable: number;
  transferTargets?: CardTransferTarget[];
  isLoadingTransferTargets?: boolean;
  onClose: () => void;
  onSuccess?: (mode: BalanceModalMode) => void;
  /** The card's organization has the card wallet (`funding-options.withdrawals_allowed`). */
  withdrawalsAllowed: boolean;
}

const formatUSD = (raw: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

export function CardBalanceModal({
  isOpen,
  initialMode,
  card,
  walletAvailable,
  cardAvailable,
  transferTargets = [],
  isLoadingTransferTargets = false,
  onClose,
  onSuccess,
  withdrawalsAllowed,
}: CardBalanceModalProps) {
  const { t } = useTranslations("cards");
  const { user } = useAuth();
  const refusalMessage = useStepUpRefusal();

  const modes = balanceModesFor(withdrawalsAllowed);
  const startMode = modes.includes(initialMode) ? initialMode : modes[0];
  const [mode, setMode] = useState<BalanceModalMode>(startMode);
  const [amount, setAmount] = useState<string>("");
  const [targetCardId, setTargetCardId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isTwoFactorChallengeOpen, setIsTwoFactorChallengeOpen] = useState(false);
  const [isTwoFactorRequiredOpen, setIsTwoFactorRequiredOpen] = useState(false);
  const [withdrawAll, setWithdrawAll] = useState(false);
  const [moveAll, setMoveAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setMode(startMode);
      setAmount("");
      setTargetCardId("");
      setValidationError(null);
      setWithdrawAll(false);
      setMoveAll(false);
    }
  }, [isOpen, startMode]);

  const parsedAmount = useMemo(() => {
    const n = Number.parseFloat(amount);
    return Number.isFinite(n) ? n : 0;
  }, [amount]);

  const cap = mode === "load" ? walletAvailable : cardAvailable;
  const isFullReturn = mode === "withdraw" && withdrawAll;
  const isFullMove = mode === "move" && moveAll;
  const isWholeBalance = isFullReturn || isFullMove;

  const validate = (): string | null => {
    if (isFullReturn) return null;
    if (isFullMove) return targetCardId ? null : t("balance_modal_target_required");
    if (!amount.trim()) return t("balance_modal_amount_required");
    if (parsedAmount <= 0) return t("balance_modal_amount_min");
    if (mode === "load" && parsedAmount > walletAvailable) return t("balance_modal_amount_over_wallet");
    if (mode !== "load" && parsedAmount > cardAvailable) return t("balance_modal_amount_over_card");
    if (mode === "move" && !targetCardId) return t("balance_modal_target_required");
    return null;
  };

  const performTopup = async (type: "topup" | "withdrawal", twoFactorCode?: string) => {
    setIsSubmitting(true);
    try {
      const response = await CardsService.topupCard(
        isFullReturn
          ? { card_id: card.id, currency: "USD", type, withdraw_all: true }
          : { card_id: card.id, amount: parsedAmount, currency: "USD", type },
        twoFactorCode ? { twoFactorCode } : undefined,
      );
      const successKey = type === "topup" ? "balance_modal_load_success" : "balance_modal_withdraw_success";
      const successAmount = response.data?.amount ?? parsedAmount;
      colocaToast.success(t(successKey, { amount: formatCardBalance(successAmount) }));
      onSuccess?.(mode);
      onClose();
    } catch (err: unknown) {
      if (isTwoFactorCodeError(err)) {
        // The challenge closed when the code left; it opens again to show the error and take another code.
        setIsTwoFactorChallengeOpen(true);
        throw stepUpRefusal(err, refusalMessage(err, t("two_factor_challenge_error_invalid_code")));
      }
      // Said by the toast; the challenge is already closed, so throwing would leave its error for next time.
      const errorKey = type === "topup" ? "balance_modal_load_error" : "balance_modal_withdraw_error";
      colocaToast.error(HttpApiService.getErrorMessage(err) || t(errorKey));
    } finally {
      setIsSubmitting(false);
    }
  };

  const performTransfer = async (twoFactorCode: string) => {
    setIsSubmitting(true);
    try {
      await CardsService.transfer(
        isFullMove
          ? { from_card_id: card.id, to_card_id: targetCardId, transfer_all: true, currency: "USD" }
          : { from_card_id: card.id, to_card_id: targetCardId, amount: parsedAmount, currency: "USD" },
        { twoFactorCode },
      );
      colocaToast.success(
        isFullMove
          ? t("balance_modal_move_success_all")
          : t("balance_modal_move_success", { amount: formatUSD(parsedAmount) }),
      );
      onSuccess?.(mode);
      onClose();
    } catch (err: unknown) {
      if (isTwoFactorCodeError(err)) {
        setIsTwoFactorChallengeOpen(true);
        throw stepUpRefusal(err, refusalMessage(err, t("two_factor_challenge_error_invalid_code")));
      }
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("balance_modal_move_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);

    if (!hasStepUpMethod(user)) {
      setIsTwoFactorRequiredOpen(true);
      return;
    }
    setIsTwoFactorChallengeOpen(true);
  };

  const submitLabel =
    mode === "load"
      ? t("balance_modal_submit_load")
      : mode === "withdraw"
        ? t("balance_modal_submit_withdraw")
        : t("balance_modal_submit_move");

  const targetOptions = transferTargets
    .filter((target) => target.id !== card.id)
    .map((target) => ({
      value: target.id,
      label: target.alias ? `${target.alias} (${target.last_4})` : `•••• ${target.last_4}`,
    }));
  const hasNoTargets = !isLoadingTransferTargets && targetOptions.length === 0;
  // The code left and the challenge closed: the provider is moving the balance.
  const sending = isSubmitting && !isTwoFactorChallengeOpen;
  const targetLast4 = transferTargets.find((target) => target.id === targetCardId)?.last_4 ?? null;
  const sendingLabel =
    mode === "load"
      ? t("balance_modal_sending_load")
      : mode === "withdraw"
        ? t("balance_modal_sending_withdraw")
        : t("balance_modal_sending_move");

  return (
    <>
      <ColocaDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && !isSubmitting) onClose();
        }}
        size="default"
        title={t("balance_modal_title")}
        footer={
          <>
            <ColocaButton variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {t("balance_modal_cancel")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {submitLabel}
            </ColocaButton>
          </>
        }
      >
        {sending ? (
          <div role="status" className="flex flex-col items-center gap-4 py-6 text-center">
            <CardBalanceTransit
              mode={mode}
              last4={card.last_4 ?? card.last4 ?? ""}
              targetLast4={targetLast4}
              amountLabel={isWholeBalance ? t("balance_modal_sending_all") : formatUSD(parsedAmount)}
              className="max-w-md"
            />
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-semibold text-foreground">{sendingLabel}</h3>
              <p className="text-sm text-muted-foreground">
                {isWholeBalance ? t("balance_modal_sending_all") : formatUSD(parsedAmount)}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {modes.length > 1 && (
              <div
                role="tablist"
                aria-label={t("balance_modal_title")}
                className={cn(
                  "grid gap-1 rounded-md bg-muted p-1",
                  modes.length === 3 ? "grid-cols-3" : "grid-cols-2",
                )}
              >
                {(
                  [
                    { id: "load", label: t("balance_modal_action_load"), icon: Plus },
                    {
                      id: "withdraw",
                      label: t("balance_modal_action_withdraw"),
                      icon: Minus,
                    },
                    {
                      id: "move",
                      label: t("balance_modal_action_move"),
                      icon: ArrowRightLeft,
                    },
                  ] as { id: BalanceModalMode; label: string; icon: typeof Plus }[]
                )
                  .filter((opt) => modes.includes(opt.id))
                  .map((opt) => {
                    const Icon = opt.icon;
                    const active = mode === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={isSubmitting}
                        onClick={() => {
                          setMode(opt.id);
                          setValidationError(null);
                          setWithdrawAll(false);
                          setMoveAll(false);
                        }}
                        className={cn(
                          "inline-flex items-center justify-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-medium transition-all",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          active
                            ? "bg-card text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                          isSubmitting && "cursor-not-allowed opacity-60",
                        )}
                      >
                        <Icon size={14} />
                        {opt.label}
                      </button>
                    );
                  })}
              </div>
            )}

            <>
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/60 px-3 py-2 text-xs">
                <span className="text-muted-foreground">
                  {mode === "load"
                    ? t("balance_modal_wallet_balance_label")
                    : t("balance_modal_card_balance_label")}
                </span>
                <span className="font-mono tabular-nums text-foreground">{formatCardBalance(cap)}</span>
              </div>

              {mode === "move" && (
                <div className="space-y-1.5">
                  <ColocaLabel htmlFor="balance-modal-target-card">
                    {t("balance_modal_target_card_label")}
                  </ColocaLabel>
                  <ColocaSelect
                    id="balance-modal-target-card"
                    value={targetCardId}
                    onValueChange={(v) => {
                      setTargetCardId(v ?? "");
                      setValidationError(null);
                    }}
                    disabled={isSubmitting || isLoadingTransferTargets || hasNoTargets}
                    placeholder={
                      isLoadingTransferTargets
                        ? t("cards_loading")
                        : t("balance_modal_target_card_placeholder")
                    }
                    options={targetOptions}
                  />
                  {hasNoTargets && (
                    <p className="text-xs text-muted-foreground">{t("balance_modal_target_empty")}</p>
                  )}
                </div>
              )}

              {mode === "withdraw" && (
                <label
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-foreground",
                    isSubmitting && "cursor-not-allowed opacity-60",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={withdrawAll}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setWithdrawAll(e.target.checked);
                      setValidationError(null);
                    }}
                    className="h-4 w-4 accent-primary"
                  />
                  {t("balance_modal_withdraw_all")}
                </label>
              )}

              {mode === "move" && (
                <ColocaCheckbox
                  checked={moveAll}
                  disabled={isSubmitting}
                  onCheckedChange={(checked) => {
                    setMoveAll(checked);
                    setValidationError(null);
                  }}
                  label={t("balance_modal_move_all")}
                  description={t("balance_modal_move_all_hint")}
                />
              )}

              <div className="flex flex-wrap gap-2">
                {PRESET_AMOUNTS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    disabled={isSubmitting || isWholeBalance}
                    onClick={() => {
                      setAmount(String(preset));
                      setValidationError(null);
                    }}
                    aria-label={t("balance_modal_preset_chip_aria", {
                      amount: formatUSD(preset),
                    })}
                    className={cn(
                      "rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground transition-colors",
                      "hover:border-primary/40 hover:bg-primary/5",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      (isSubmitting || isWholeBalance) && "cursor-not-allowed opacity-60",
                      parsedAmount === preset && "border-primary bg-primary/10 text-primary",
                    )}
                  >
                    ${preset.toLocaleString("en-US")}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5">
                <ColocaLabel htmlFor="balance-modal-amount">{t("balance_modal_amount_label")}</ColocaLabel>
                <ColocaInput
                  id="balance-modal-amount"
                  type="number"
                  inputMode="decimal"
                  value={amount}
                  onValueChange={(v) => {
                    setAmount(v);
                    setValidationError(null);
                  }}
                  disabled={isSubmitting || isWholeBalance}
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground">{t("balance_modal_amount_helper")}</p>
              </div>
            </>

            {validationError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                {validationError}
              </div>
            )}
          </div>
        )}
      </ColocaDialog>

      <TwoFactorChallengeModal
        open={isTwoFactorChallengeOpen}
        onOpenChange={setIsTwoFactorChallengeOpen}
        onSubmit={async (code) => {
          // The card provider answers in seconds: close the challenge and show the money moving instead of
          // leaving it on "Verifying…". A wrong code opens it again (see the catch blocks above). Asking for an
          // email code only gets "sent" back, so the challenge stays up for it.
          if (code !== STEP_UP_EMAIL_REQUEST) setIsTwoFactorChallengeOpen(false);
          if (mode === "move") {
            await performTransfer(code);
            return;
          }
          await performTopup(mode === "load" ? "topup" : "withdrawal", code);
        }}
      />

      <TwoFactorRequiredModal
        open={isTwoFactorRequiredOpen}
        onOpenChange={setIsTwoFactorRequiredOpen}
        reason={mode === "move" ? "move_card" : "topup_card"}
        onGoToSettings={() => {
          setIsTwoFactorRequiredOpen(false);
          onClose();
          navigate("/app/settings?section=seguridad");
        }}
        onEnrolled={() => {
          // The PIN was created right there: the movement the person asked for is next, behind it.
          setIsTwoFactorRequiredOpen(false);
          setIsTwoFactorChallengeOpen(true);
        }}
      />
    </>
  );
}
